import type { Logger } from '~/interfaces/Logger'
import { boundLogContext } from '~/utils/boundLogContext'
import { isEngineeringEnvironment } from '~/utils/isEngineeringEnvironment'
import { Normalization } from '~/utils/normalization'
import { redactForLog } from '~/utils/redactForLog'

type LogLevel = 'error' | 'warn' | 'info' | 'debug'

const LEVEL_RANK: Record<LogLevel, number> = { debug: 0, info: 1, warn: 2, error: 3 }

interface LogRecord {
  readonly level: LogLevel
  readonly component: string
  readonly operation: string
  readonly data?: unknown
}

/** Single read of `import.meta.dev` so tests can substitute it. */
const isDevelopmentEnvironment = (): boolean => import.meta.dev

/** The only module permitted to use `console.*`; the map is exhaustive over `LogLevel`. */
const CONSOLE_SINK: Record<LogLevel, (record: LogRecord) => void> = {
  error: (record) => {
    console.error(record)
  },
  warn: (record) => {
    console.warn(record)
  },
  info: (record) => {
    console.info(record)
  },
  debug: (record) => {
    console.debug(record)
  }
}

type RelayContextValue = string | number | boolean | null

// A nested object/array context value is stringified, not dropped, so the signal still reaches the backend.
function relayContextValue(value: unknown): RelayContextValue {
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  ) {
    return value
  }

  try {
    return JSON.stringify(value)
  } catch {
    return String(value)
  }
}

// A bare Error has no enumerable name/message; its stack comes from resolveRelayStack.
function buildRelayContext(redactedData: unknown): Record<string, RelayContextValue> | undefined {
  if (redactedData === undefined) return undefined

  if (redactedData instanceof Error) {
    return { name: redactedData.name, message: redactedData.message }
  }

  if (Normalization.isRecord(redactedData)) {
    const context: Record<string, RelayContextValue> = {}
    for (const [key, value] of Object.entries(redactedData)) {
      context[key] = relayContextValue(value)
    }
    return context
  }

  return { value: relayContextValue(redactedData) }
}

function relayMessage(operation: string, redactedData: unknown): string {
  let candidate = ''
  if (redactedData instanceof Error) candidate = redactedData.message
  else if (typeof redactedData === 'string') candidate = redactedData
  else if (Normalization.isRecord(redactedData) && typeof redactedData['message'] === 'string') {
    candidate = redactedData['message']
  }
  return candidate === '' ? operation : candidate
}

// Read from the unredacted error because redactForLog strips the stack.
function resolveRelayStack(originalData: unknown): string | undefined {
  if (!(originalData instanceof Error) || !isEngineeringEnvironment()) return undefined
  try {
    return originalData.stack
  } catch {
    return undefined
  }
}

// Configurable via NUXT_PUBLIC_CLIENT_LOG_LEVEL; falls back to 'debug' locally, 'warn' otherwise.
function resolveRelayFloor(): LogLevel {
  const configured: unknown = useRuntimeConfig().public.clientLogLevel
  if (
    configured === 'debug' ||
    configured === 'info' ||
    configured === 'warn' ||
    configured === 'error'
  ) {
    return configured
  }

  return isDevelopmentEnvironment() ? 'debug' : 'warn'
}

// The Nuxt plugin owns browser buffering and direct SSR forwarding; logging never blocks its caller.
function sendRelay(
  component: string,
  level: LogLevel,
  operation: string,
  redactedData: unknown,
  originalData: unknown
): void {
  try {
    if (LEVEL_RANK[level] < LEVEL_RANK[resolveRelayFloor()]) return

    const baseContext = buildRelayContext(redactedData)
    const stack = resolveRelayStack(originalData)
    const rawContext = stack !== undefined ? { ...(baseContext ?? {}), stack } : baseContext
    const bounded = rawContext !== undefined ? boundLogContext(rawContext) : undefined

    useNuxtApp().$clientLogRelay.push({
      event: `${component}.${operation}`,
      level,
      message: relayMessage(operation, redactedData).slice(0, 2000).toWellFormed(),
      ...(bounded !== undefined ? { context: bounded.context } : {})
    })
  } catch {
    // Logging remains available outside an active Nuxt application context.
  }
}

/** Hands the record straight to `console.*`; never held in state, so SSR hydration cannot serialize it. */
function log(component: string, level: LogLevel, operation: string, data?: unknown): void {
  const isDevelopment = isDevelopmentEnvironment()

  if (!isDevelopment && (level === 'debug' || level === 'info')) return

  const record: LogRecord = {
    level,
    component,
    operation,
    data: redactForLog(data, !isDevelopment)
  }

  CONSOLE_SINK[level](record)
  sendRelay(component, level, operation, record.data, data)
}

/** The single logging entry point; `debug`/`info` are no-ops outside development. */
export function useLogger(component: string): Logger {
  const emit = (level: LogLevel) => (operation: string, data?: unknown) => {
    log(component, level, operation, data)
  }

  return { error: emit('error'), warn: emit('warn'), info: emit('info'), debug: emit('debug') }
}
